import { computed, Injectable, signal } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { TasksRepository } from '../tasks.repository'
import { Task } from '../models/task.entity'
import { TaskSearchParams } from '../models/task-search.model'
import { Page } from '../../../models/util.model'

interface TasksListingState {
  filters?: TaskSearchParams
  data?: Task[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
}

const SUPPLIER_ORDERS_INITIAL_STATE: TasksListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class TasksListingStore {
  constructor(private tasksRepository: TasksRepository) {}

  private state = signal<TasksListingState>(SUPPLIER_ORDERS_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly tasks = computed(() => this.state().data || [])
  readonly totalCount = computed(() => this.state().totalCount || 0)

  // Effects

  async search(params?: TaskSearchParams, returnAll?: boolean): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.tasksRepository.searchTasks$(params ?? {}, returnAll).subscribe({
        next: (data) => {
          resolve()
          this.setData(data)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  /**
   * REDUCERS
   */

  setData(data: Page<Task>) {
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setTotalCount(count: number) {
    this.state.set({
      ...this.state(),
      totalCount: count,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: TaskSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: TaskSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: TaskSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof TaskSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }
}
